/**
 * Where the reader is inside an article, and how that gets into the URL.
 *
 * Both the progress tracker and the arrival scroll need to agree on "which
 * section am I in", so the answer lives here rather than in each of them.
 */

/**
 * How far below the viewport top a heading still counts as reached.
 *
 * Deliberately larger than the `scroll-margin-top: 6rem` that anchored
 * headings use: a heading scrolled to the top parks at exactly 96px, and
 * comparing against 96 makes sub-pixel rounding decide whether it counts —
 * which showed up as arriving at a section and being told you were in the
 * previous one.
 */
export const HEADING_OFFSET_PX = 120;

/** The last heading the reader has scrolled past, if they have passed any. */
export function currentSectionId(): string | undefined {
  const headings = Array.from(
    document.querySelectorAll<HTMLElement>('article h2[id], article h3[id]')
  );

  let found: string | undefined;
  for (const heading of headings) {
    if (heading.getBoundingClientRect().top > HEADING_OFFSET_PX) break;
    found = heading.id;
  }
  return found;
}

/**
 * Point the address bar at a section.
 *
 * `replaceState` rather than a router navigation: this runs while scrolling,
 * and every one of those must not become a history entry the back button has
 * to walk through. It also leaves `useLocation` untouched, so nothing
 * re-renders and no scrolling is triggered.
 */
export function writeSectionHash(id: string | undefined): void {
  const next = id ? `#${id}` : '';
  if (window.location.hash === next) return;

  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + window.location.search + next
  );
}
